import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  X, Target, Beef, Wheat, Droplet, HeartPulse, XCircle, Lightbulb,
} from 'lucide-react';
import { setWithExpiry } from '../../utils/storage';

const RULES = [
  { icon: Target, color: '#E91E63',
    fr: { t: 'Ne dépassez pas vos calories', d: "C'est la règle n°1 pour perdre ou maintenir votre poids. Restez sous votre objectif chaque jour." },
    en: { t: "Don't exceed your calories", d: 'This is rule #1 to lose or maintain weight. Stay under your goal every day.' } },
  { icon: Beef, color: '#EC4899',
    fr: { t: 'Remplissez bien vos protéines', d: 'Atteignez votre cible : les protéines préservent vos muscles et coupent la faim. Poulet, poisson, skyr, œufs…' },
    en: { t: 'Hit your protein target', d: 'Protein preserves muscle and curbs hunger. Chicken, fish, skyr, eggs…' } },
  { icon: Wheat, color: '#FBBF24',
    fr: { t: 'Ne dépassez pas vos glucides', d: 'Gardez-les surtout autour de votre entraînement pour l\'énergie, et évitez les sucres rapides en excès.' },
    en: { t: "Don't exceed your carbs", d: 'Keep them mostly around your workout for energy, and avoid excess fast sugars.' } },
  { icon: Droplet, color: '#10B981',
    fr: { t: 'Ne dépassez pas vos lipides', d: 'Privilégiez les bonnes graisses (huile d\'olive, avocat, noix) et limitez fromage, charcuterie et fritures.' },
    en: { t: "Don't exceed your fats", d: 'Favor good fats (olive oil, avocado, nuts) and limit cheese, cured meats and fried foods.' } },
];

const AVOID = {
  fr: [
    'Les boissons sucrées (sodas, jus, sirops) — des calories liquides inutiles.',
    'Le grignotage sucré (chocolat, biscuits, viennoiseries).',
    'Les fritures et le fast-food — beaucoup de gras cachés.',
    'Sauter des repas puis se rattraper le soir.',
  ],
  en: [
    'Sugary drinks (sodas, juices, syrups) — useless liquid calories.',
    'Sweet snacking (chocolate, cookies, pastries).',
    'Fried foods and fast-food — lots of hidden fat.',
    'Skipping meals then overeating at night.',
  ],
};

const SCORE_TIERS = [
  { range: '80–100', color: '#10B981', fr: 'Excellent', en: 'Excellent' },
  { range: '60–79', color: '#84CC16', fr: 'Bon', en: 'Good' },
  { range: '40–59', color: '#FBBF24', fr: 'Moyen', en: 'Average' },
  { range: '0–39', color: '#F97316', fr: 'À améliorer', en: 'To improve' },
];

const SCORE_TIPS = {
  fr: [
    'Ajoutez des légumes et des fruits à chaque repas.',
    'Choisissez des protéines maigres (poulet, poisson, skyr, œufs).',
    'Préférez les céréales complètes (riz, pâtes, pain complets).',
    'Réduisez les sucres rapides et les fritures.',
    'Buvez de l\'eau plutôt que des sodas.',
  ],
  en: [
    'Add vegetables and fruit to every meal.',
    'Choose lean protein (chicken, fish, skyr, eggs).',
    'Prefer wholegrain cereals (brown rice, pasta, bread).',
    'Cut down on fast sugars and fried foods.',
    'Drink water instead of sodas.',
  ],
};

export const GuideModal = ({ open, onClose, en }) => {
  const [dontShowAgain, setDontShowAgain] = useState(false);

  const handleClose = () => {
    // L'utilisateur a effectivement consulté le guide → marquer comme vu (expire après 12 mois)
    setWithExpiry('bodyup_guide_seen', '1');
    if (dontShowAgain) {
      setWithExpiry('bodyup_guide_dismissed', '1');
    }
    onClose();
  };

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          className="fixed inset-0 z-[70] flex items-end sm:items-center justify-center p-0 sm:p-4"
          initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
          data-testid="guide-modal"
        >
          <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={handleClose} />
        <motion.div
          initial={{ y: 40, opacity: 0 }} animate={{ y: 0, opacity: 1 }} exit={{ y: 40, opacity: 0 }}
          transition={{ type: 'spring', stiffness: 300, damping: 30 }}
          className="relative w-full sm:max-w-2xl bg-zinc-900 border border-white/10 rounded-t-3xl sm:rounded-3xl p-6 max-h-[90vh] overflow-y-auto"
        >
          <div className="flex items-center justify-between mb-5">
            <div className="flex items-center gap-2">
              <Lightbulb className="w-5 h-5 text-yellow-400" />
              <h3 className="text-lg font-bold text-white">{en ? 'Guide & tips' : 'Guide & conseils'}</h3>
            </div>
            <button onClick={handleClose} className="p-2 rounded-lg hover:bg-white/10 text-zinc-400" data-testid="guide-close"><X className="w-5 h-5" /></button>
          </div>

          {/* 4 règles d'or */}
          <p className="text-xs font-bold tracking-wider text-primary mb-3">{en ? 'YOUR 4 GOLDEN RULES' : "VOS 4 RÈGLES D'OR"}</p>
          <div className="grid sm:grid-cols-2 gap-3 mb-6">
            {RULES.map((r, i) => {
              const c = en ? r.en : r.fr;
              return (
                <div key={i} className="rounded-2xl bg-zinc-800/50 border border-white/10 p-4">
                  <div className="flex items-center gap-2.5 mb-1.5">
                    <div className="w-9 h-9 rounded-xl flex items-center justify-center shrink-0" style={{ background: `${r.color}22` }}>
                      <r.icon className="w-4.5 h-4.5" style={{ color: r.color }} />
                    </div>
                    <p className="font-bold text-white text-sm">{c.t}</p>
                  </div>
                  <p className="text-xs text-zinc-400 leading-relaxed">{c.d}</p>
                </div>
              );
            })}
          </div>

          {/* À éviter */}
          <p className="text-xs font-bold tracking-wider text-red-400 mb-3">{en ? 'WHAT TO AVOID' : 'À ÉVITER'}</p>
          <div className="space-y-2 mb-6">
            {(en ? AVOID.en : AVOID.fr).map((a, i) => (
              <div key={i} className="flex items-start gap-2.5">
                <XCircle className="w-4 h-4 text-red-400 mt-0.5 shrink-0" />
                <p className="text-sm text-zinc-300">{a}</p>
              </div>
            ))}
          </div>

          {/* Score santé */}
          <div className="rounded-2xl bg-gradient-to-br from-primary/10 to-zinc-900 border border-white/10 p-5" data-testid="guide-health-score">
            <div className="flex items-center gap-2 mb-2">
              <HeartPulse className="w-5 h-5 text-primary" />
              <h4 className="font-bold text-white">{en ? 'How the Health Score works' : 'Comment fonctionne le Score Santé'}</h4>
            </div>
            <p className="text-sm text-zinc-300 leading-relaxed mb-3">
              {en
                ? 'Your Health Score (0–100) is the nutritional quality of each food, averaged and weighted by its calories. The more whole, nutritious foods you eat, the higher it climbs.'
                : 'Votre Score Santé (0–100) correspond à la qualité nutritionnelle de chaque aliment, moyennée et pondérée par ses calories. Plus vous mangez d\'aliments bruts et nutritifs, plus il monte.'}
            </p>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mb-4">
              {SCORE_TIERS.map((s, i) => (
                <div key={i} className="rounded-xl bg-zinc-800/60 border border-white/10 p-2 text-center">
                  <p className="text-xs font-bold" style={{ color: s.color }}>{s.range}</p>
                  <p className="text-[11px] text-zinc-400">{en ? s.en : s.fr}</p>
                </div>
              ))}
            </div>
            <p className="text-xs font-bold tracking-wider text-emerald-400 mb-2">{en ? 'EASY WAYS TO IMPROVE IT' : 'ASTUCES FACILES POUR L\'AMÉLIORER'}</p>
            <div className="space-y-1.5">
              {(en ? SCORE_TIPS.en : SCORE_TIPS.fr).map((t, i) => (
                <div key={i} className="flex items-start gap-2">
                  <span className="text-emerald-400 mt-0.5">✓</span>
                  <p className="text-sm text-zinc-300">{t}</p>
                </div>
              ))}
            </div>
          </div>

          {/* Checkbox "Ne plus afficher" */}
          <div className="mt-5 flex items-center gap-2.5 px-1">
            <input
              type="checkbox"
              id="dont-show-guide"
              checked={dontShowAgain}
              onChange={(e) => setDontShowAgain(e.target.checked)}
              className="w-4 h-4 rounded border-white/20 bg-zinc-800 text-primary focus:ring-2 focus:ring-primary cursor-pointer"
            />
            <label htmlFor="dont-show-guide" className="text-sm text-zinc-400 cursor-pointer select-none">
              {en ? "Don't show this again" : "Ne plus afficher ce guide"}
            </label>
          </div>

          <button
            onClick={handleClose}
            className="mt-4 w-full py-3 rounded-xl font-bold text-white bg-gradient-to-r from-primary to-pink-500 hover:scale-[1.01] transition-transform"
            data-testid="guide-got-it"
          >
            {en ? "Got it, let's go!" : "J'ai compris, c'est parti !"}
          </button>
        </motion.div>
      </motion.div>
    )}
  </AnimatePresence>
  );
};

export default GuideModal;
